import { ConnectorImage } from "../ConnectorImage";
import { useZero } from "@rocicorp/zero/react";
import { useLayoutEffect, useRef, useState } from "react";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import {
  GITHUB_APP_INSTALLATION_ERROR,
  GITHUB_APP_PENDING_INSTALLATION,
} from "../../../shared/connectors/github/manifestInstallation";
import { sharedMutators } from "../../../shared/mutators";
import { useSessionTenantOrigin } from "../../hooks/useSessionTenantOrigin";
import {
  queueConnectorEdit,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { Icon } from "../Icon";
import { IntegrationButton } from "../IntegrationButton";
import { Link } from "../Link";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import {
  Actions,
  AuthorizedButton,
  AuthorizedInfo,
  AuthorizedLabel,
  CheckIcon,
  IconButtonLink,
} from "./authorizedChip";
import type { App } from "./Form";
import { useOauthPopupWindow } from "./useOauthPopupWindow";

type Props = {
  app: App;
  definition: ConnectorDefinition;
  disabled?: boolean;
};

export function GithubAppManifestFieldItem({
  app,
  definition,
  disabled,
}: Props) {
  const popup = useOauthPopupWindow();
  const z = useZero();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestOrigin = window.location.origin;
  const callbackOrigin = useSessionTenantOrigin();
  const current = useRef({ app, disabled });
  useLayoutEffect(() => {
    current.current = { app, disabled };
    return () => {
      current.current = { app, disabled: true };
    };
  }, [app, disabled]);

  const { appId, appSlug, installationId, organizationLogin } =
    app.configuration;
  const hasPrivateKey = app.secretKeys?.includes("privateKey");
  const missingPrivateKey = Boolean(appId && !hasPrivateKey);
  const installed = Boolean(appId && installationId && hasPrivateKey);
  const pendingInstallationId = app.metadata[GITHUB_APP_PENDING_INSTALLATION];
  const installationError = app.metadata[GITHUB_APP_INSTALLATION_ERROR];
  // Only a manifest-created app has a slug to resume the install picker with; a
  // manually-entered app falls through to creating a new one.
  const canResumeInstall = Boolean(appId && appSlug);
  // GitHub has no manifest field or API for app logos; uploading is manual.
  const settingsUrl = appSlug
    ? organizationLogin
      ? `https://github.com/organizations/${encodeURIComponent(organizationLogin)}/settings/apps/${encodeURIComponent(appSlug)}`
      : `https://github.com/settings/apps/${encodeURIComponent(appSlug)}`
    : null;

  const start = async () => {
    if (disabled || isPending || missingPrivateKey) return;
    setIsPending(true);
    setError(null);
    const recordInstallation = async (id: string) => {
      const saved = await queueConnectorEdit(app.tenantId, app.id, async () => {
        if (current.current.disabled) return false;
        const update = await withConnectorDestinationProof(
          current.current.app,
          {
            configurationPatch: { installationId: id },
          }
        );
        if (current.current.disabled) return false;
        const result = await z.mutate(
          update.destinationProofs?.privateKey
            ? sharedMutators.updateConnectedApp(update)
            : sharedMutators.recordGithubAppInstallation({
                connectedAppId: app.id,
                installationId: id,
              })
        ).server;
        return result.type === "success";
      }).catch(() => false);
      if (!saved)
        setError("Couldn’t record the GitHub App installation. Try again.");
    };
    const flow = async () => {
      if (pendingInstallationId) {
        await recordInstallation(pendingInstallationId);
        return;
      }
      let url: string;
      if (canResumeInstall) {
        url = `https://github.com/apps/${encodeURIComponent(appSlug!)}/installations/new`;
      } else {
        const startUrl = new URL(
          `${requestOrigin}/connectors/github-app/manifest/start`
        );
        startUrl.searchParams.set("app_id", app.id);
        url = startUrl.toString();
      }
      const result = await popup.flow(url, callbackOrigin, {
        provider: definition.identifier,
        connectedAppId: app.id,
      });
      if (result && "error" in result) setError(result.error);
      if (result && "success" in result && result.installationId)
        await recordInstallation(result.installationId);
    };
    await flow().finally(() => setIsPending(false));
  };

  if (installed) {
    return (
      <div>
        <AuthorizedButton
          style={settingsUrl ? undefined : { padding: "6px 20px" }}
        >
          <AuthorizedLabel>
            <ConnectorImage src={definition.logo} alt={definition.name} />
            <Text variant="text-regular-md">GitHub App installed</Text>
          </AuthorizedLabel>
          {settingsUrl && (
            <Actions>
              <Tooltip content="Open GitHub app settings">
                <IconButtonLink
                  href={settingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="arrow-up-right" size={16} />
                </IconButtonLink>
              </Tooltip>
            </Actions>
          )}
        </AuthorizedButton>
        <AuthorizedInfo>
          <CheckIcon />
          <Text variant="text-small" color="var(--text-secondary)">
            {appSlug ? `github.com/apps/${appSlug}` : `App ID ${appId}`}
          </Text>
        </AuthorizedInfo>
      </div>
    );
  }

  return (
    <div>
      <IntegrationButton
        type="button"
        onClick={start}
        disabled={disabled || isPending || missingPrivateKey}
        isLoading={isPending}
        logo={definition.logo}
        text={
          pendingInstallationId
            ? installationError
              ? "Retry GitHub App installation"
              : "Finish GitHub App installation"
            : canResumeInstall
              ? "Install GitHub App"
              : "Create GitHub App"
        }
      />
      {missingPrivateKey && (
        <AuthorizedInfo>
          <Icon name="info" size={12} />
          <Text variant="text-small" color="var(--text-secondary)">
            Upload the private key below to reconnect this GitHub App.
            {settingsUrl && (
              <>
                {" "}
                Generate a new key in{" "}
                <Link
                  to={settingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub App settings
                </Link>
                .
              </>
            )}
          </Text>
        </AuthorizedInfo>
      )}
      {(error || installationError) && (
        <AuthorizedInfo>
          <Icon name="info" size={12} />
          <Text variant="text-small" color="var(--text-error)">
            {error || installationError}
          </Text>
        </AuthorizedInfo>
      )}
    </div>
  );
}
